// Gemeinsame Abfragen. Jede trägt die Mandanten-ID im Schlüssel (qk) und wertet `error` aus.

import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { ApiError, callFunction } from "./api";
import { useTenant } from "./auth";
import { qk } from "./query-keys";
import { supabase } from "./supabase";

export interface Department {
  id: string;
  name: string;
  sort_order: number;
  active: boolean;
}

export interface Employee {
  id: string;
  user_id: string | null;
  personnel_number: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  department_id: string;
  entry_date: string | null;
  exit_date: string | null;
  has_bonus: boolean;
  notes: string | null;
}

export interface ScheduleTemplate {
  id: string;
  name: string;
  description: string | null;
  weekday_minutes: number[];
  active: boolean;
}

export interface Terminal {
  id: string;
  department_id: string;
  connection_id: string | null;
  name: string;
  kind: string;
  external_device_id: string | null;
  external_location_id: string | null;
  active: boolean;
  last_event_at: string | null;
}

export interface TenantRow {
  id: string;
  name: string;
  timezone: string;
  holiday_regions: string[];
  default_language: string;
}

export const fullName = (e: Pick<Employee, "first_name" | "last_name">) => `${e.first_name} ${e.last_name}`;

export function useTenantRow() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.tenant(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase.from("tenants").select("id, name, timezone, holiday_regions, default_language").single();
      if (error) throw error;
      return data as TenantRow;
    },
  });
}

export function useDepartments() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.departments(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase.from("departments").select("id, name, sort_order, active").order("sort_order").order("name");
      if (error) throw error;
      return data as Department[];
    },
  });
}

export function useEmployees() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.employees(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("id, user_id, personnel_number, first_name, last_name, email, department_id, entry_date, exit_date, has_bonus, notes")
        .order("last_name")
        .order("first_name");
      if (error) throw error;
      return data as Employee[];
    },
  });
}

export function useTemplates() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.templates(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase.from("schedule_templates").select("id, name, description, weekday_minutes, active").order("name");
      if (error) throw error;
      return data as ScheduleTemplate[];
    },
  });
}

export function useTerminals() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.terminals(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("terminals")
        .select("id, department_id, connection_id, name, kind, external_device_id, external_location_id, active, last_event_at")
        .order("name");
      if (error) throw error;
      return data as Terminal[];
    },
  });
}

export function useRuleSets() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.ruleSets(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase.from("rule_sets").select("id, valid_from, rules, created_at").order("valid_from", { ascending: false });
      if (error) throw error;
      return data as { id: string; valid_from: string; rules: Record<string, unknown>; created_at: string }[];
    },
  });
}

export function useBalances() {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.balances(tenant_id),
    queryFn: async () => {
      const { data, error } = await supabase.rpc("time_account_balances");
      if (error) throw error;
      return new Map(((data ?? []) as { employee_id: string; balance_minutes: number }[]).map((r) => [r.employee_id, r.balance_minutes]));
    },
  });
}

/** Fehler als Toast: bleibt stehen (kein Auto-Schließen) und zeigt die Support-Kennung. */
export function notifyError(error: unknown) {
  const code = error instanceof ApiError ? error.code : (error as { code?: string })?.code;
  const message = code && i18n.exists(`errors.${code}`) ? i18n.t(`errors.${code}`) : i18n.t("errors.generic");
  const detail = error instanceof ApiError ? (error.requestId ? `${i18n.t("errors.request_id")}: ${error.requestId}` : error.message) : undefined;
  toast.error(message, { description: detail, duration: Infinity, closeButton: true });
}

/**
 * Aktion über eine Edge Function mit anschließendem Neuladen der betroffenen Abfragen.
 * Erfolg zeigt einen kurzen Toast, Fehler einen stehenbleibenden (notifyError).
 */
export function useFunctionAction<TInput extends Record<string, unknown>, TResult = unknown>(
  fn: string,
  options: { invalidate?: (input: TInput) => QueryKey[]; success?: (result: TResult, input: TInput) => string | null } = {},
) {
  const queryClient = useQueryClient();
  const { tenant_id } = useTenant();
  return useMutation({
    mutationFn: (input: TInput) => callFunction<TResult>(fn, input),
    onSuccess: async (result, input) => {
      const keys = options.invalidate?.(input) ?? [[tenant_id]];
      await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      const message = options.success ? options.success(result, input) : i18n.t("common.saved");
      if (message) toast.success(message);
    },
    onError: notifyError,
  });
}
